import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { DocumentsTab } from '../components/profile/DocumentsTab';
import { JobTab } from '../components/profile/JobTab';
import { PersonalTab } from '../components/profile/PersonalTab';
import { ProfileAction, ProfileHeader } from '../components/profile/ProfileHeader';
import { ProfileStickyBar } from '../components/profile/ProfileStickyBar';
import { RequestTimeOffModal } from '../components/profile/RequestTimeOffModal';
import { TimeOffTab } from '../components/profile/TimeOffTab';
import { VitalsCard } from '../components/profile/VitalsCard';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useHRStore } from '../hooks/useHRStore';
import type { Navigate, ProfileTab, PtoRequest } from '../types/hr';
import { displayName } from '../utils/employees';
import { getProfileDetails } from '../utils/profile';
interface EmployeeProfileProps {
  employeeId: string;
  initialTab?: ProfileTab;
  requests: PtoRequest[];
  onNavigate: Navigate;
}

// Top bar (64px) + compact bar (56px) + breathing room.
const STICKY_OFFSET = 64 + 56 + 16;
export function EmployeeProfile({
  employeeId,
  initialTab = 'personal',
  requests,
  onNavigate
}: EmployeeProfileProps) {
  const {
    employees
  } = useHRStore();
  const [tab, setTab] = useState<ProfileTab>(initialTab);
  const [editing, setEditing] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [autoUpload, setAutoUpload] = useState(false);
  const [compact, setCompact] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Directory order (Employee # ascending) — same default order as the Employees page.
  const index = Math.max(0, employees.findIndex(e => e.id === employeeId));
  const total = employees.length;
  const employee = employees[index];
  const details = getProfileDetails(employee);
  useEffect(() => {
    const onScroll = () => {
      const el = headerRef.current;
      if (el) setCompact(el.getBoundingClientRect().bottom < 72);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const go = (delta: number) => {
    const next = employees[(index + delta + total) % total];
    onNavigate({
      page: 'profile',
      employeeId: next.id,
      tab
    });
  };
  const changeTab = (next: ProfileTab) => {
    setTab(next);
    if (next !== 'personal') setEditing(false);
  };
  const selectTabFromBar = (next: ProfileTab) => {
    changeTab(next);
    const el = contentRef.current;
    if (el) window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET,
      behavior: 'smooth'
    });
  };
  const handleAction = (action: ProfileAction) => {
    if (action === 'edit') {
      setTab('personal');
      setEditing(true);
    }
    if (action === 'timeoff') setRequestOpen(true);
    if (action === 'upload') {
      changeTab('documents');
      setAutoUpload(true);
    }
    if (action === 'terminate') setTerminateOpen(true);
  };
  return <div data-id="element-947" data-mp="177tvtlr23o8c">
      <div ref={headerRef} data-id="element-948" data-mp="177k49tqd590j">
        <ProfileHeader employee={employee} position={index + 1} total={total} tab={tab} onTabChange={changeTab} onPrev={() => go(-1)} onNext={() => go(1)} onBack={() => onNavigate({
        page: 'employees'
      })} onAction={handleAction} data-id="element-949" data-mp="135p7n2jdnjt3" />
      </div>

      <ProfileStickyBar visible={compact} employee={employee} tab={tab} position={index + 1} total={total} onTabChange={selectTabFromBar} onPrev={() => go(-1)} onNext={() => go(1)} data-id="element-950" data-mp="1qu8gbj6c9ylp" />

      <div ref={contentRef} className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[300px_minmax(0,1fr)]" data-id="element-951" data-mp="ah1fcn07nu61">
        <div className={`lg:sticky lg:self-start ${compact ? 'lg:top-[136px]' : 'lg:top-24'}`} data-id="element-952" data-mp="1ydpsx4ogc82f">
          <VitalsCard employee={employee} onNavigate={onNavigate} data-id="element-953" data-mp="234fxifp206w2" />
        </div>
        <div className="min-w-0" data-id="element-954" data-mp="236iwvqc3jcdg">
          {tab === 'personal' && <PersonalTab key={employee.id} employee={employee} details={details} editing={editing} onEdit={() => setEditing(true)} onEditDone={() => setEditing(false)} data-id="element-955" data-mp="1pem7vv5q5ila" />}
          {tab === 'job' && <JobTab key={employee.id} employee={employee} details={details} data-id="element-956" data-mp="2h4qodiqd5lh8" />}
          {tab === 'timeoff' && <TimeOffTab key={employee.id} employee={employee} requests={requests} onRequest={() => setRequestOpen(true)} onNavigate={onNavigate} data-id="element-957" data-mp="2m2s6o1fytgrn" />}
          {tab === 'documents' && <DocumentsTab key={employee.id} employeeId={employee.id} autoOpenUpload={autoUpload} onAutoOpenHandled={() => setAutoUpload(false)} data-id="element-958" data-mp="2d1kmenaaaggk" />}
        </div>
      </div>

      <RequestTimeOffModal open={requestOpen} employee={employee} onClose={() => setRequestOpen(false)} data-id="element-959" data-mp="11o02kbn3ucez" />

      <Modal open={terminateOpen} onClose={() => setTerminateOpen(false)} title={`Terminate ${displayName(employee)}?`} description="This starts the offboarding checklist and ends access on the termination date." footer={<>
            <Button onClick={() => setTerminateOpen(false)} data-id="element-961" data-mp="y5st71gcatsy">Cancel</Button>
            <Button variant="danger" onClick={() => {
        setTerminateOpen(false);
        toast('Termination drafted', {
          description: 'Saved as a draft — nothing is final until you confirm the date.'
        });
      }} data-id="element-962" data-mp="y3q3yjn0h4pa">
              Start termination
            </Button>
          </>} data-id="element-960" data-mp="t5okhxlob3da">
        <p className="text-sm text-slate-600" data-id="element-963" data-mp="fe9s505vlptk">
          You'll be asked for a termination date, reason and final PTO payout before anything is finalized. This is sample data — no real record will change.
        </p>
      </Modal>
    </div>;
}